import { OUTLOOK_TEMPLATE_ID } from '@electron/shared/outlook-sign-in.types';
import { useMemo } from 'react';
import { toast } from 'sonner';

import { ModalsManager } from '@/components/modals/modalsManager';
import { Dialog } from '@/components/ui/dialog';
import type { BundledMcpTemplate } from '@/features/integrations/bundled-mcp-templates';
import type { OnDeviceMcpCatalogEntry } from '@/features/integrations/integrations.types';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import { trackCustomMcpServerAdded } from '@/integrations/integrations.utils';

import { AddServerModal } from './add-server-modal';
import { AppleNotesConnectModal } from './apple-notes-connect-modal';
import { APPLE_NOTES_TEMPLATE_ID } from './apple-notes-setup';
import {
  getLocalPathSetup,
  type TrustedLocalPathSetup,
} from './local-path-setup';
import { LocalPathSetupModal } from './local-path-setup-modal';
import { LogseqConnectModal } from './logseq-connect-modal';
import { LOGSEQ_CONNECT, LOGSEQ_TEMPLATE_ID } from './logseq-setup';
import { OneClickConnectModal } from './one-click-connect-modal';
import {
  isOneClickCatalogEntry,
  oneClickServerFromBundled,
  oneClickServerFromCatalog,
  type OneClickServer,
} from './one-click-server';
import { OutlookConnectModal } from './outlook-connect-modal';
import { useAddServerForm } from './use-add-server-form';
import { useLocalPathSetup } from './use-local-path-setup';
import { useOneClickConnect } from './use-one-click-connect';
import { useOutlookSignIn } from './use-outlook-sign-in';
import { useTestedConnect } from './use-tested-connect';
import { XeroConnectModal } from './xero-connect-modal';
import { XERO_CONNECT, XERO_TEMPLATE_ID } from './xero-setup';

type LocalMcpAddServerModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  catalogEntry?: OnDeviceMcpCatalogEntry;
  bundledTemplate?: BundledMcpTemplate;
  initialUrl?: string;
};

type FocusedLocalPathModalProps = LocalMcpAddServerModalProps & {
  catalogEntry: OnDeviceMcpCatalogEntry;
  setup: TrustedLocalPathSetup;
};

type OneClickConnectFlowProps = Pick<
  LocalMcpAddServerModalProps,
  'open' | 'onOpenChange'
> & {
  server: OneClickServer;
  onConnected: () => void;
};

type CatalogConnectFlowProps = LocalMcpAddServerModalProps & {
  catalogEntry: OnDeviceMcpCatalogEntry;
};

const GenericLocalMcpAddServerModal = ({
  open,
  onOpenChange,
  catalogEntry,
  bundledTemplate,
  initialUrl,
}: LocalMcpAddServerModalProps) => {
  const close = () => onOpenChange?.(false);
  const form = useAddServerForm({
    initialCatalogEntry: catalogEntry,
    initialBundledTemplate: bundledTemplate,
    initialUrl,
    onSaved: () => {
      trackCustomMcpServerAdded(
        catalogEntry?.id ?? bundledTemplate?.id,
        catalogEntry ? 'catalog' : bundledTemplate ? 'bundled' : 'custom',
        form.transport
      );
      toast.success('Local MCP server added');
      close();
    },
  });

  const backToCloud = () => {
    close();
    ModalsManager.show(MCPAddServerModal, {});
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        className="w-auto max-w-none border-none bg-transparent p-0 shadow-none !z-[100] no-drag"
      >
        <AddServerModal
          form={form}
          initialStep={catalogEntry ? 2 : bundledTemplate ? 0 : 1}
          onBack={catalogEntry || bundledTemplate ? undefined : backToCloud}
          onCancel={close}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};

const FocusedLocalPathModal = ({
  open,
  onOpenChange,
  catalogEntry,
  setup,
}: FocusedLocalPathModalProps): React.ReactElement => {
  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && !form.cancel()) return;
    onOpenChange?.(nextOpen);
  }

  const form = useLocalPathSetup({
    catalogEntry,
    setup,
    onConnected: () => {
      trackCustomMcpServerAdded(catalogEntry.id, 'catalog', 'stdio');
      toast.success(`${catalogEntry.name} connected`);
      handleOpenChange(false);
    },
  });

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        className="w-auto max-w-none border-none bg-transparent p-0 shadow-none !z-[100] no-drag"
      >
        <LocalPathSetupModal
          setup={setup}
          form={form}
          onCancel={() => handleOpenChange(false)}
        />
      </Dialog.Content>
    </Dialog.Root>
  );
};

/**
 * Bundled servers and catalog packages with nothing to fill in get a dialog
 * that only does what they actually need: install with visible progress,
 * show what to expect on this device, and connect.
 */
const OneClickConnectFlowModal = ({
  open,
  onOpenChange,
  server,
  onConnected,
}: OneClickConnectFlowProps): React.ReactElement => {
  const form = useOneClickConnect({
    server,
    onConnected: () => {
      onConnected();
      toast.success(`${server.name} connected`);
      onOpenChange?.(false);
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    // Escape and backdrop clicks must respect the same lock as the X button:
    // closing mid-save would orphan a half-written server entry.
    if (!nextOpen && !form.cancel()) return;
    onOpenChange?.(nextOpen);
  }
  const close = () => handleOpenChange(false);

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        className="w-auto max-w-none border-none bg-transparent p-0 shadow-none !z-[100] no-drag"
      >
        {server.id === APPLE_NOTES_TEMPLATE_ID ? (
          <AppleNotesConnectModal
            server={server}
            form={form}
            onCancel={close}
          />
        ) : (
          <OneClickConnectModal server={server} form={form} onCancel={close} />
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
};

const BundledConnectFlowModal = ({
  bundledTemplate,
  ...props
}: LocalMcpAddServerModalProps & {
  bundledTemplate: BundledMcpTemplate;
}): React.ReactElement => {
  const server = useMemo(
    () => oneClickServerFromBundled(bundledTemplate),
    [bundledTemplate]
  );
  return (
    <OneClickConnectFlowModal
      {...props}
      server={server}
      onConnected={() =>
        trackCustomMcpServerAdded(bundledTemplate.id, 'bundled', 'bundled')
      }
    />
  );
};

const CatalogConnectFlowModal = ({
  catalogEntry,
  ...props
}: LocalMcpAddServerModalProps & {
  catalogEntry: OnDeviceMcpCatalogEntry;
}): React.ReactElement => {
  const server = useMemo(
    () => oneClickServerFromCatalog(catalogEntry),
    [catalogEntry]
  );
  return (
    <OneClickConnectFlowModal
      {...props}
      server={server}
      onConnected={() =>
        trackCustomMcpServerAdded(catalogEntry.id, 'catalog', 'stdio')
      }
    />
  );
};

/**
 * Outlook signs in with Microsoft inside this dialog, so it stays open after
 * the server is saved until the sign-in finishes or the user leaves.
 */
const OutlookConnectFlowModal = ({
  open,
  onOpenChange,
  bundledTemplate,
}: LocalMcpAddServerModalProps & {
  bundledTemplate: BundledMcpTemplate;
}): React.ReactElement => {
  const server = useMemo(
    () => oneClickServerFromBundled(bundledTemplate),
    [bundledTemplate]
  );
  const form = useOneClickConnect({
    server,
    onConnected: () =>
      trackCustomMcpServerAdded(bundledTemplate.id, 'bundled', 'bundled'),
  });
  const { signIn, retry } = useOutlookSignIn(form.phase === 'connected');

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && !form.cancel()) return;
    onOpenChange?.(nextOpen);
  }

  return (
    <ConnectDialog open={open} onOpenChange={handleOpenChange}>
      <OutlookConnectModal
        server={server}
        form={form}
        signIn={signIn}
        onRetrySignIn={retry}
        onCancel={() => handleOpenChange(false)}
        onDone={() => {
          toast.success('Outlook connected');
          handleOpenChange(false);
        }}
      />
    </ConnectDialog>
  );
};

function ConnectDialog({
  open,
  onOpenChange,
  children,
}: {
  open?: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content
        showCloseButton={false}
        className="w-auto max-w-none border-none bg-transparent p-0 shadow-none !z-[100] no-drag"
      >
        {children}
      </Dialog.Content>
    </Dialog.Root>
  );
}

const LogseqConnectFlowModal = ({
  open,
  onOpenChange,
  catalogEntry,
}: CatalogConnectFlowProps): React.ReactElement => {
  const form = useTestedConnect({
    spec: LOGSEQ_CONNECT,
    catalogEntry,
    onConnected: () => {
      trackCustomMcpServerAdded(catalogEntry.id, 'catalog', 'stdio');
      toast.success('Logseq connected');
      onOpenChange?.(false);
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && !form.canCancel) return;
    onOpenChange?.(nextOpen);
  }

  return (
    <ConnectDialog open={open} onOpenChange={handleOpenChange}>
      <LogseqConnectModal
        catalogEntry={catalogEntry}
        form={form}
        onCancel={() => handleOpenChange(false)}
      />
    </ConnectDialog>
  );
};

const XeroConnectFlowModal = ({
  open,
  onOpenChange,
  catalogEntry,
}: CatalogConnectFlowProps): React.ReactElement => {
  const form = useTestedConnect({
    spec: XERO_CONNECT,
    catalogEntry,
    onConnected: () => {
      trackCustomMcpServerAdded(catalogEntry.id, 'catalog', 'stdio');
      toast.success('Xero connected');
      onOpenChange?.(false);
    },
  });

  function handleOpenChange(nextOpen: boolean) {
    if (!nextOpen && !form.canCancel) return;
    onOpenChange?.(nextOpen);
  }

  return (
    <ConnectDialog open={open} onOpenChange={handleOpenChange}>
      <XeroConnectModal
        catalogEntry={catalogEntry}
        form={form}
        onCancel={() => handleOpenChange(false)}
      />
    </ConnectDialog>
  );
};

export const LocalMcpAddServerModal = (
  props: LocalMcpAddServerModalProps
): React.ReactElement => {
  const setup = getLocalPathSetup(props.catalogEntry?.id);
  if (setup && props.catalogEntry) {
    return (
      <FocusedLocalPathModal
        {...props}
        catalogEntry={props.catalogEntry}
        setup={setup}
      />
    );
  }

  if (props.catalogEntry?.id === LOGSEQ_TEMPLATE_ID) {
    return (
      <LogseqConnectFlowModal {...props} catalogEntry={props.catalogEntry} />
    );
  }

  if (props.catalogEntry?.id === XERO_TEMPLATE_ID) {
    return (
      <XeroConnectFlowModal {...props} catalogEntry={props.catalogEntry} />
    );
  }

  if (props.catalogEntry && isOneClickCatalogEntry(props.catalogEntry)) {
    return (
      <CatalogConnectFlowModal {...props} catalogEntry={props.catalogEntry} />
    );
  }

  if (props.bundledTemplate?.id === OUTLOOK_TEMPLATE_ID) {
    return (
      <OutlookConnectFlowModal
        {...props}
        bundledTemplate={props.bundledTemplate}
      />
    );
  }

  // A template carrying `requirements` still needs the full form to collect
  // its args/env, so it keeps the generic flow.
  if (props.bundledTemplate && !props.bundledTemplate.requirements?.length) {
    return (
      <BundledConnectFlowModal
        {...props}
        bundledTemplate={props.bundledTemplate}
      />
    );
  }

  return <GenericLocalMcpAddServerModal {...props} />;
};
